import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Plus, Search, Upload, Users, MapPin, Briefcase } from 'lucide-react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  listJobDescriptions,
  parseJobDescriptionUpload,
  type ApiJobDescriptionStatus,
  type ApiJobDescriptionSummary,
} from '../../libs/api/jobDescriptionService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';

const mapApiStatusToLabel = (status: ApiJobDescriptionStatus): string => {
  if (status === 'ACTIVE') return 'Active Posting';
  if (status === 'DRAFT') return 'Draft';
  return 'Closed';
};

const mapStatusFilterToApi = (value: string): ApiJobDescriptionStatus | undefined => {
  if (value === 'active') return 'ACTIVE';
  if (value === 'draft') return 'DRAFT';
  if (value === 'closed') return 'CLOSE';
  return undefined;
};

const getStatusStyles = (status: ApiJobDescriptionStatus) => {
  if (status === 'ACTIVE') {
    return {
      dot: 'bg-success',
      text: 'text-success',
    };
  }

  if (status === 'DRAFT') {
    return {
      dot: 'bg-warning',
      text: 'text-warning-muted-foreground',
    };
  }

  return {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
  };
};

const JDManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [departmentFilter, setDepartmentFilter] = React.useState('all');
  const [isUploadingJd, setIsUploadingJd] = React.useState(false);
  const jdUploadInputRef = React.useRef<HTMLInputElement>(null);

  const handleUploadJdClick = () => {
    if (isUploadingJd) return;
    jdUploadInputRef.current?.click();
  };

  const handleUploadJdChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsUploadingJd(true);
    try {
      const result = await parseJobDescriptionUpload(file);
      navigate('/jobs/new', { state: { prefill: result.jd } });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to read JD file'));
    } finally {
      setIsUploadingJd(false);
    }
  };

  const {
    data: jdResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['job-descriptions', { page: 1, page_size: 100 }],
    queryFn: () => listJobDescriptions({ page: 1, page_size: 100 }),
  });

  React.useEffect(() => {
    if (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load job descriptions'));
    }
  }, [error]);

  const jds = React.useMemo<ApiJobDescriptionSummary[]>(
    () => jdResponse?.items ?? [],
    [jdResponse],
  );

  const activeCount = React.useMemo(() => jds.filter((jd) => jd.status === 'ACTIVE').length, [jds]);
  const draftCount = React.useMemo(() => jds.filter((jd) => jd.status === 'DRAFT').length, [jds]);
  const totalApplicants = React.useMemo(
    () => jds.reduce((sum, jd) => sum + (jd.candidate_count ?? 0), 0),
    [jds],
  );
  const departmentOptions = React.useMemo(
    () =>
      Array.from(new Set(jds.map((jd) => jd.department).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [jds],
  );
  const filteredJds = React.useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    return jds.filter((jd) => {
      const matchesSearch =
        keyword.length === 0 ||
        jd.title.toLowerCase().includes(keyword) ||
        jd.department.toLowerCase().includes(keyword) ||
        jd.location.toLowerCase().includes(keyword);
      const matchesStatus =
        statusFilter === 'all' || jd.status === mapStatusFilterToApi(statusFilter);
      const matchesDepartment = departmentFilter === 'all' || jd.department === departmentFilter;

      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [departmentFilter, jds, searchTerm, statusFilter]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-main">Job Descriptions</h1>
          <p className="text-text-subtle">
            Manage and create job requirements for your recruitment pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleUploadJdClick} disabled={isUploadingJd}>
            {isUploadingJd ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {isUploadingJd ? 'Reading JD file...' : 'Upload JD'}
          </Button>
          <input
            ref={jdUploadInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            disabled={isUploadingJd}
            onChange={(event) => {
              void handleUploadJdChange(event);
            }}
          />
          <Button onClick={() => navigate('/jobs/new')}>
            <Plus size={16} /> Create New JD
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Total JDs',
            value: jds.length,
            icon: FileText,
            color: 'text-primary',
            onClick: () => {
              setSearchTerm('');
              setStatusFilter('all');
              setDepartmentFilter('all');
            },
          },
          {
            label: 'Active Postings',
            value: activeCount,
            icon: Briefcase,
            color: 'text-success',
            onClick: () => setStatusFilter('active'),
          },
          {
            label: 'Total Applicants',
            value: totalApplicants,
            icon: Users,
            color: 'text-info',
            onClick: () => {
              setSearchTerm('');
              setStatusFilter('all');
              setDepartmentFilter('all');
            },
          },
          {
            label: 'Drafts',
            value: draftCount,
            icon: FileText,
            color: 'text-warning-muted-foreground',
            onClick: () => setStatusFilter('draft'),
          },
        ].map((stat, i) => (
          <button
            key={i}
            type="button"
            onClick={stat.onClick}
            className="bg-card p-4 rounded-xl border border-border-subtle shadow-sm flex items-center gap-4 text-left hover:shadow-md transition-all"
          >
            <div className={`p-2 rounded-lg bg-bg-main ${stat.color}`}>
              <stat.icon size={20} />
            </div>
            <div>
              <div className="text-sm font-semibold text-text-subtle uppercase tracking-wider">
                {stat.label}
              </div>
              <div className="text-2xl font-bold text-text-main">{stat.value}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 bg-card p-4 rounded-xl border border-border-subtle shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" size={18} />
          <Input
            type="text"
            placeholder="Search job descriptions..."
            className="bg-bg-main pl-10"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-52 bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departmentOptions.map((department) => (
              <SelectItem key={department} value={department}>
                {department}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* JD Grid */}
      {isLoading ? (
        <div className="rounded-xl border border-border-subtle bg-card p-6 text-sm text-text-subtle">
          Loading job descriptions...
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredJds.map((jd) => {
          const statusStyles = getStatusStyles(jd.status);

          return (
            <motion.button
              type="button"
              key={jd.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate(`/jobs/${jd.id}/edit`)}
              className="bg-card border border-border-subtle rounded-xl shadow-sm hover:shadow-md transition-all group overflow-hidden text-left cursor-pointer"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-light rounded-lg text-primary">
                      <Briefcase size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-text-main group-hover:text-primary transition-colors">
                        {jd.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-text-subtle mt-0.5">
                        <span className="font-medium text-primary">{jd.department}</span>
                        <span>•</span>
                        <span>Created {new Date(jd.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm text-text-subtle">
                    <MapPin size={14} /> {jd.location || 'N/A'}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-subtle">
                    <Users size={14} /> {jd.candidate_count ?? 0} Applicants
                  </div>
                </div>
              </div>
              <div className="px-6 py-3 bg-bg-main border-t border-border-subtle flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${statusStyles.dot}`} />
                  <span
                    className={`text-xs font-bold uppercase tracking-widest ${statusStyles.text}`}
                  >
                    {mapApiStatusToLabel(jd.status)}
                  </span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {!isLoading && filteredJds.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-card p-6 text-sm text-text-subtle">
          No job descriptions match the current search or filters.
        </div>
      ) : null}
    </div>
  );
};

export default JDManagement;
